import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { ImperativePanelGroupHandle } from 'react-resizable-panels';
import { useAppSelector } from '../../../app/hooks';
import {
  selectCurrentTaskAIStatus,
  selectIsCurrentTaskUnsupervised,
} from '../../../redux/features/taskRouter/taskRouter.selector';
import { selectUserProfile } from '../../../selectors/user';
import {
  getLayoutPreference,
  resolvePaneUserKey,
  saveLayoutPreference,
} from '../../../utils/local-storage';
import { useLayoutPreference } from '../../Layout/LayoutPreferenceContext';
import {
  MAX_PANE_SIZE,
  MIN_PANE_SIZE,
} from '../../Layout/LayoutPreferenceDialog';

// Widest the left pane can go under the 25–50% per-pane clamp: left maxed, the
// center + cart at their minimum but still visible. Used for the unsupervised
// task-router arrival, where the left pane opens expanded so the transcript gets
// the room while the center menu + cart stay on screen.
export const EXPANDED_LEFT_PANE_SIZES: [number, number, number] = [
  MAX_PANE_SIZE,
  MIN_PANE_SIZE,
  MIN_PANE_SIZE,
];

/**
 * The pane layout to apply on task-router arrival, or null to leave the
 * default sizes untouched. An UNSUPERVISED AI task arrival opens with the left
 * pane expanded to 50% so the transcript gets the room while the center menu +
 * cart stay visible. A SUPERVISED arrival returns null, so the default split is
 * kept.
 */
export const getArrivalPaneLayout = (
  currentTaskAIStatus: unknown,
  isCurrentTaskUnsupervised: boolean
): typeof EXPANDED_LEFT_PANE_SIZES | null =>
  Boolean(currentTaskAIStatus) && isCurrentTaskUnsupervised
    ? EXPANDED_LEFT_PANE_SIZES
    : null;

/**
 * The layout to apply once, on mount, when arriving at the console. An explicit
 * saved layout preference — set via the Layout preference dialog OR by dragging a
 * divider (see persistLayoutOnDragEnd) — always wins, so the agent keeps their
 * split instead of the transcript auto-expanding. With no saved preference it
 * falls back to the unsupervised-arrival expansion (see getArrivalPaneLayout);
 * null means "leave the default sizes untouched".
 */
export const getMountLayout = (
  savedPreference: [number, number, number] | null,
  currentTaskAIStatus: unknown,
  isCurrentTaskUnsupervised: boolean
): [number, number, number] | null =>
  savedPreference ??
  getArrivalPaneLayout(currentTaskAIStatus, isCurrentTaskUnsupervised);

/**
 * Resizable-pane wiring for the 3-pane console. The horizontal split has a
 * SINGLE source of truth — the `hitl-layout-preference` key — so a manual drag
 * and an explicit dialog "Apply" persist to the same place and are honoured
 * identically on the next arrival. (The PanelGroup's own `autoSaveId` is
 * deliberately NOT used for this group; it would be a second, diverging store.)
 * The nested left/right vertical stacks keep their own `autoSaveId` via
 * paneStorageKey.
 */
const usePaneLayout = () => {
  const userProfile = useAppSelector(selectUserProfile);
  const userKey = resolvePaneUserKey(userProfile);
  const paneStorageKey = `hitl-console-panes:${userKey}`;
  const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);

  const currentTaskAIStatus = useAppSelector(selectCurrentTaskAIStatus);
  const isCurrentTaskUnsupervised = useAppSelector(
    selectIsCurrentTaskUnsupervised
  );

  const { reportLiveSizes, registerPaneSetter } = useLayoutPreference();

  // Let the Layout preference dialog (hosted in the provider) resize these live
  // panes while they're mounted; clear the setter on unmount so Apply/Reset from
  // the lobby only persist to storage.
  useEffect(() => {
    registerPaneSetter((sizes) => panelGroupRef.current?.setLayout(sizes));
    return () => registerPaneSetter(null);
  }, [registerPaneSetter]);

  // One-time cleanup: earlier this group persisted via PanelGroup autoSaveId,
  // leaving a `react-resizable-panels:hitl-console-panes:<user>` entry that
  // diverged from the layout-preference key on drag. That store is gone now;
  // drop the stale key so it can't confuse debugging. (Suffixed left/right-stack
  // keys are a different, still-active group and are left alone.)
  useEffect(() => {
    try {
      localStorage.removeItem(`react-resizable-panels:${paneStorageKey}`);
    } catch {
      /* storage unavailable — nothing to clean up */
    }
  }, [paneStorageKey]);

  // Persist the split when the agent finishes dragging a divider. Reads the
  // authoritative sizes off the PanelGroup handle at drag-end and writes them to
  // the same key the dialog uses, so a drag is a first-class "preference".
  const persistLayoutOnDragEnd = useCallback(
    (isDragging: boolean) => {
      if (isDragging) return;
      const sizes = panelGroupRef.current?.getLayout();
      if (sizes && sizes.length === 3) {
        saveLayoutPreference(userKey, [sizes[0], sizes[1], sizes[2]]);
      }
    },
    [userKey]
  );

  // On arrival, apply the once-on-mount layout: an explicit saved preference
  // wins, else an unsupervised AI arrival expands the transcript. useLayoutEffect
  // (not useEffect) applies it before paint so there's no flash of the default
  // split first. Runs once on arrival; the agent drags the handles / edits the
  // dialog after.
  useLayoutEffect(() => {
    const mountLayout = getMountLayout(
      getLayoutPreference(userKey),
      currentTaskAIStatus,
      isCurrentTaskUnsupervised
    );
    if (mountLayout) panelGroupRef.current?.setLayout(mountLayout);
    // Mount-only: the task flags are set in completeTRSetup before this mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    paneStorageKey,
    panelGroupRef,
    reportLiveSizes,
    persistLayoutOnDragEnd,
  };
};

export default usePaneLayout;
